import { Badge, Modal, Skeleton } from '@indipia/ui-core'
import type { OrderVendorRow } from '../../api/orderApi'
import { ASSIGN_REASON_TEXT, formatCandidateMetrics, ORDER_MESSAGE } from '../../constants/materialOrder.logic'
import { useVendorCandidates } from '../../hooks/useMaterialOrders'
import { ErrorBanner } from '../AsyncState'

// 자동배정 근거 모달 — 상세창 카드 '자동매칭' 태그·C-3 목록 '자동배정 ⓘ' 가 같이 쓴다.
// ① 배정 사유(행에 저장된 assignReasonCode) ② 지금 기준 재계산(V1 evidence) ③ 후보 목록(서버 순서·지표). 값이 없으면 채우지 않는다.

interface Props {
  open: boolean
  onClose: () => void
  taskId: number
  row: OrderVendorRow | null
  vendorName: string
  /** 머리 부제의 공종명(없으면 생략) */
  workTypeName?: string
}

const SECTION = 'mt-3 text-xs font-semibold'

export function MaterialEvidenceModal({ open, onClose, taskId, row, vendorName, workTypeName }: Props) {
  const { data, status, reload } = useVendorCandidates(taskId, open)
  const subtitle = [workTypeName, vendorName].filter(Boolean).join(' · ')

  const reasonText = row?.assignReasonCode ? ASSIGN_REASON_TEXT[row.assignReasonCode] : undefined
  const ev = data?.evidence
  const ruleVendor = ev && ev.ruleVendorId != null ? data?.candidates.find(c => c.vendorId === ev.ruleVendorId) : undefined

  return (
    <Modal open={open} onClose={onClose} title={ORDER_MESSAGE.evidenceTitle} size="md">
      <div onMouseDown={e => e.stopPropagation()}>
        {subtitle && <div className="text-sm font-bold" style={{ color: 'var(--shell-on-surface)' }}>{subtitle}</div>}
        <div className="mb-2 text-xs" style={{ color: 'var(--shell-on-surface-2)' }}>{ORDER_MESSAGE.evidenceSub}</div>

        {status === 'loading' && (
          <div aria-busy="true" className="flex flex-col gap-2">
            {[0, 1, 2].map(i => <Skeleton key={i} className="h-7" />)}
          </div>
        )}
        {status === 'error' && <ErrorBanner message={ORDER_MESSAGE.evidenceFailed} onRetry={reload} />}

        {status === 'ready' && data && ev && (
          <>
            <div className={SECTION} style={{ color: 'var(--shell-on-surface-2)' }}>{ORDER_MESSAGE.evidenceReasonHead}</div>
            <div className="mt-1 text-sm" style={{ color: 'var(--shell-on-surface)' }}>{reasonText ?? ORDER_MESSAGE.evidenceReasonUnknown}</div>

            <div className={SECTION} style={{ color: 'var(--shell-on-surface-2)' }}>{ORDER_MESSAGE.evidenceRecalcHead}</div>
            <div className="mt-1 text-sm" style={{ color: 'var(--shell-on-surface)' }}>
              {!ev.directOrder
                ? ORDER_MESSAGE.evidenceNotDirect
                : !ev.matchesCurrent && ruleVendor
                  ? ORDER_MESSAGE.evidenceRecalc(ruleVendor.vendorName)
                  : ev.matchesCurrent ? ORDER_MESSAGE.vendorCurrent : ''}
            </div>

            <div className={SECTION} style={{ color: 'var(--shell-on-surface-2)' }}>{ORDER_MESSAGE.evidenceCandidatesHead}</div>
            <ul className="mt-1 flex flex-col gap-1.5 list-none p-0 m-0">
              {data.candidates.map(c => (
                <li key={c.vendorId} className={c.declinedBefore ? 'opacity-50' : undefined}>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <b className="text-sm" style={{ color: 'var(--shell-on-surface)' }}>{c.vendorName}</b>
                    {c.mainVendor && <Badge status="info">{ORDER_MESSAGE.vendorMain}</Badge>}
                    {c.vendorId === data.currentVendorId && <Badge status="success">{ORDER_MESSAGE.vendorCurrent}</Badge>}
                    {c.declinedBefore && <Badge status="mute">{ORDER_MESSAGE.vendorDeclinedBefore}</Badge>}
                  </div>
                  <div className="text-xs" style={{ color: 'var(--shell-on-surface-2)' }}>{formatCandidateMetrics(c)}</div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Modal>
  )
}
